// src/components/CalculadoraRiders.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  getFinancieras,
} from "../helpers/rutaFinancieras";

import {
  MEDIOS_PAGO_VENTA,
} from "../helpers/rutaPagoVentas";

import {
  calcularVentaCombinadaRiders,
  calcularPagoDesdeProductoRiders,
  getFinancieraCalculadoraById,
  resolverConfiguracionFinancieraCalculadora,
  TAX_RIDERS_PORCENTAJE,
} from "../helpers/calculadoraRiders";

import "../css/calculadora-riders.css";

/* =========================================================
   UTILIDADES
========================================================= */

const makeTempId = () =>
  `calc-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;

const makePagoInicial = () => ({
  tempId:
    makeTempId(),

  medioPago:
    "EFECTIVO EN TIENDA",

  montoCobradoUSD:
    "",

  financiera:
    "",

  cuotasFinanciacion:
    "",
});

const clean = (v) =>
  String(v ?? "").trim();

const toNumber = (
  v,
  def = 0
) => {
  const n =
    Number(v);

  return Number.isFinite(n)
    ? n
    : def;
};

const formatUSD = (
  valor
) => {
  const numero =
    Number(valor || 0);

  return numero.toLocaleString(
    "es-AR",
    {
      style:
        "currency",

      currency:
        "USD",

      minimumFractionDigits:
        2,
    }
  );
};

/* =========================================================
   COMPONENTE
========================================================= */

const CalculadoraRiders = ({
  onCerrar,
  onAplicar,
  financierasExternas,
  saldoPendientePaso4USD = 0,
  saldoPendienteSinTaxPaso4USD = 0,
  usarSaldoAutomaticoPaso4 = false,
}) => {
  const [
    financieras,
    setFinancieras,
  ] = useState(() =>
    Array.isArray(
      financierasExternas
    )
      ? financierasExternas
      : []
  );

  const [
    loadingFinancieras,
    setLoadingFinancieras,
  ] = useState(false);

  const [
    errorCarga,
    setErrorCarga,
  ] = useState("");

  const [
    pagos,
    setPagos,
  ] = useState(() => [
    makePagoInicial(),
  ]);

  const [
    montoCopiadoId,
    setMontoCopiadoId,
  ] = useState("");

  const round2 = (n) =>
    Math.round(
      (Number(n) || 0) * 100
    ) / 100;

  /* =======================================================
     CARGAR FINANCIERAS
  ======================================================= */

  useEffect(() => {
    /*
      Cuando la calculadora está dentro
      de Venta Rápida reutilizamos
      las financieras que ya cargó
      el Paso 4.
    */
    if (
      Array.isArray(
        financierasExternas
      )
    ) {
      setFinancieras(
        financierasExternas
      );

      setLoadingFinancieras(
        false
      );

      setErrorCarga(
        ""
      );

      return;
    }

    let alive =
      true;

    const cargar =
      async () => {
        try {
          setLoadingFinancieras(
            true
          );

          setErrorCarga(
            ""
          );

          const resp =
            await getFinancieras({
              page: 1,
              limit: 300,
            });

          if (!alive) {
            return;
          }

          const lista =
            Array.isArray(
              resp?.financieras
            )
              ? resp.financieras
              : [];

          setFinancieras(
            lista
          );
        } catch (err) {
          console.error(
            "Error cargando financieras calculadora:",
            err
          );

          if (alive) {
            setErrorCarga(
              "No se pudieron cargar las financieras."
            );
          }
        } finally {
          if (alive) {
            setLoadingFinancieras(
              false
            );
          }
        }
      };

    cargar();

    return () => {
      alive = false;
    };
  }, [
    financierasExternas,
  ]);

  /* =======================================================
     CÁLCULO

     POR AHORA SOLO:
     DESDE LO COBRADO
  ======================================================= */

  const resultado =
    useMemo(
      () =>
        calcularVentaCombinadaRiders(
          pagos,
          financieras
        ),
      [
        pagos,
        financieras,
      ]
    );



  /* =========================================================
   COMPLETAR AUTOMATICAMENTE SALDO DEL PASO 4
========================================================= */

const calcularMontoSugeridoPaso4 = (
  pago = {},
  idxActual = -1,
  pagosBase = []
) => {
  /*
    Este automático solamente trabaja
    cuando la calculadora está integrada
    al Paso 4.
  */
  if (
    typeof onAplicar !== "function"
  ) {
    return 0;
  }

  /*
    Saldo que recibimos desde Paso 4.

    Ejemplo al comenzar:
    Total con Tax = 5350
    Saldo sin Tax = 5000
  */
  const saldoPaso4USD =
    round2(
      Math.max(
        0,
        toNumber(
          saldoPendientePaso4USD,
          0
        )
      )
    );

  const saldoPaso4SinTaxUSD =
    round2(
      Math.max(
        0,
        toNumber(
          saldoPendienteSinTaxPaso4USD,
          0
        )
      )
    );

  if (
    saldoPaso4USD <= 0
  ) {
    return 0;
  }

  /*
    =====================================================
    PAGOS QUE YA ESTÁN DENTRO DE LA CALCULADORA

    Excluimos el pago actual.

    Ejemplo:
    Pago 1 ZELLE = 2000

    Ese pago ya cubre:
    Aplicado: 2000
    Producto: 1869.16
    =====================================================
  */
  const otrosPagos =
    (Array.isArray(pagosBase)
      ? pagosBase
      : []
    ).filter(
      (_, idx) =>
        idx !== idxActual
    );

  const resultadoOtros =
    calcularVentaCombinadaRiders(
      otrosPagos,
      financieras
    );

  const aplicadoOtrosUSD =
    round2(
      Math.max(
        0,
        toNumber(
          resultadoOtros
            ?.totalAplicadoUSD,
          0
        )
      )
    );

  const productoOtrosUSD =
    round2(
      Math.max(
        0,
        toNumber(
          resultadoOtros
            ?.precioProductoUSD,
          0
        )
      )
    );

  /*
    Saldo REAL restante después de
    descontar lo que ya cargamos
    dentro de esta misma calculadora.

    Ejemplo:
    5350 - Zelle 2000 = 3350
  */
  const saldoNormalUSD =
    round2(
      Math.max(
        saldoPaso4USD -
          aplicadoOtrosUSD,
        0
      )
    );

  /*
    Saldo restante SIN Tax.

    Ejemplo:
    5000 - 1869.16 = 3130.84
  */
  const saldoSinTaxUSD =
    round2(
      Math.max(
        saldoPaso4SinTaxUSD -
          productoOtrosUSD,
        0
      )
    );

  if (
    saldoNormalUSD <= 0
  ) {
    return 0;
  }

  const medio =
    clean(
      pago?.medioPago
    ).toUpperCase();

  if (
    medio !==
    "CREDITO FINANCIERA"
  ) {
    return 0;
  }

  const financiera =
    getFinancieraCalculadoraById(
      pago?.financiera,
      financieras
    );

  if (!financiera) {
    return 0;
  }

  const config =
    resolverConfiguracionFinancieraCalculadora(
      financiera,
      toNumber(
        pago?.cuotasFinanciacion,
        0
      )
    );

  /*
    Si depende de cuotas,
    esperamos selección válida.
  */
  if (
    config?.requiereCuotas
  ) {
    const cuotas =
      toNumber(
        pago?.cuotasFinanciacion,
        0
      );

    const disponibles =
      Array.isArray(
        config?.cuotasDisponibles
      )
        ? config.cuotasDisponibles
        : [];

    if (
      cuotas <= 0 ||
      (
        disponibles.length > 0 &&
        !disponibles.includes(
          cuotas
        )
      )
    ) {
      return 0;
    }
  }

  /*
    Convertimos el saldo pendiente
    nuevamente a precio de producto.

    CON TAX:
    3350 / 1.07 = 3130.84

    SIN TAX:
    usamos directamente el saldo
    de producto restante.
  */
  const montoProductoUSD =
    config?.aplicaTax !== false
      ? round2(
          saldoNormalUSD /
            (
              1 +
              TAX_RIDERS_PORCENTAJE /
                100
            )
        )
      : saldoSinTaxUSD;

  const calculado =
    calcularPagoDesdeProductoRiders(
      {
        ...pago,

        montoProductoUSD:
          String(
            montoProductoUSD
          ),
      },
      financieras
    );

  if (
    calculado?.error ||
    !calculado?.valido
  ) {
    return 0;
  }

  return round2(
    toNumber(
      calculado
        ?.montoCobradoUSD,
      0
    )
  );

};

const copiarPrecioVenta = async () => {
  const monto =
    toNumber(
      resultado?.precioProductoUSD,
      0
    );

  if (monto <= 0) {
    return;
  }

  try {
    await navigator.clipboard.writeText(
      monto.toFixed(2)
    );

    setMontoCopiadoId(
      "PRECIO_VENTA"
    );

    window.setTimeout(() => {
      setMontoCopiadoId("");
    }, 1500);
  } catch (err) {
    console.error(
      "Error copiando precio:",
      err
    );
  }
};

const copiarMontoFinanciera = async (
  pago
) => {
  const monto =
    toNumber(
      pago?.montoCobradoUSD,
      0
    );

  if (monto <= 0) {
    return;
  }

  const texto =
    monto.toFixed(2);

  try {
    await navigator.clipboard.writeText(
      texto
    );

    setMontoCopiadoId(
      pago.tempId
    );

    window.setTimeout(
      () => {
        setMontoCopiadoId("");
      },
      1500
    );
  } catch (err) {
    console.error(
      "Error copiando monto:",
      err
    );
  }
};

  /* =======================================================
     HANDLERS
  ======================================================= */

  const cambiarPago = (
    idx,
    campo,
    valor
  ) => {
    setPagos((prev) =>
      prev.map(
        (
          pago,
          i
        ) => {
          if (
            i !== idx
          ) {
            return pago;
          }

          const next = {
            ...pago,

            [campo]:
              valor,
          };

          /*
            Cambio medio:
            limpiamos financiera
            y cuotas.
          */
          if (
            campo ===
            "medioPago"
          ) {
            next.financiera =
              "";

            next.cuotasFinanciacion =
              "";
          }

          /*
            Cambio financiera:
            limpiamos cuotas.
          */
          if (
            campo ===
            "financiera"
          ) {
            next.cuotasFinanciacion =
              "";
          }

          /*
          Si venimos del Paso 4 con un saldo
          pendiente y el vendedor selecciona
          financiera/cuotas, calculamos solo
          cuánto debe fondear.
        */
        if (
          usarSaldoAutomaticoPaso4 &&
          [
            "financiera",
            "cuotasFinanciacion",
          ].includes(campo)
        ) {
          const montoSugeridoUSD =
            calcularMontoSugeridoPaso4(
              next,
              idx,
              prev
            );

          if (
            montoSugeridoUSD > 0
          ) {
            next.montoCobradoUSD =
              String(
                montoSugeridoUSD
              );
          }
        }

          return next;
        }
      )
    );
  };

  const agregarPago =
    () => {
      setPagos(
        (prev) => [
          ...prev,

          makePagoInicial(),
        ]
      );
    };

  const quitarPago = (
    idx
  ) => {
    setPagos(
      (prev) => {
        if (
          prev.length <=
          1
        ) {
          return prev;
        }

        return prev.filter(
          (
            _,
            i
          ) =>
            i !== idx
        );
      }
    );
  };

  const limpiar =
    () => {
      setPagos([
        makePagoInicial(),
      ]);
    };

  /* =======================================================
     APLICAR AL PASO 4
  ======================================================= */

  const puedeAplicar =
    typeof onAplicar ===
      "function" &&
    resultado?.valido ===
      true &&
    toNumber(
      resultado
        ?.totalCobradoUSD,
      0
    ) > 0;

  const aplicarAlPaso4 =
    () => {
      if (
        !puedeAplicar
      ) {
        return;
      }

      onAplicar({
        pagos,

        resultado,
      });
    };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="cr-shell">

      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="cr-header">

        <div>

          <div className="cr-title-row">

            <span className="cr-icon">
              🧮
            </span>

            <h2>
              Calculadora Riders
            </h2>

          </div>

          <p>
            Ingresá cómo pagó o fondeó
            el cliente y calculamos el
            precio real de la venta.
          </p>

        </div>

        {onCerrar && (
          <button
            type="button"
            className="cr-btn-close"
            onClick={
              onCerrar
            }
          >
            ✕
          </button>
        )}

      </div>

      {/* ===================================================
          AVISO
      =================================================== */}

      <div className="cr-info">

        <strong>
          Desde lo cobrado.
        </strong>{" "}

        Ingresá el dinero real recibido
        o fondeado.

        {typeof onAplicar ===
          "function" && (
          <>
            {" "}
            Al finalizar podés volcar
            los datos directamente al
            Paso 4.
          </>
        )}

      </div>

      {toNumber(
        saldoPendientePaso4USD,
        0
      ) > 0 && (
        <div className="cr-paso4-saldo">

          <span>
            Saldo pendiente en Paso 4
          </span>

          <strong>
            {formatUSD(
              saldoPendientePaso4USD
            )}
          </strong>

          {usarSaldoAutomaticoPaso4 && (
            <small>
              Elegí la financiera.
              La calculadora completará
              automáticamente cuánto
              debe fondear.
            </small>
          )}

        </div>
      )}

      {errorCarga && (
        <div className="cr-error">
          {errorCarga}
        </div>
      )}

      {/* ===================================================
          CABECERA PAGOS
      =================================================== */}

      <div className="cr-section-header">

        <div>

          <h3>
            Formas de pago
          </h3>

          <span>
            Podés combinar todos
            los medios necesarios.
          </span>

        </div>

        <button
          type="button"
          className="cr-btn-add"
          onClick={
            agregarPago
          }
        >
          + Agregar pago
        </button>

      </div>

      {/* ===================================================
          PAGOS
      =================================================== */}

      <div className="cr-payments">

        {pagos.map(
          (
            pago,
            idx
          ) => {
            const detalle =
              resultado
                ?.pagosCalculados?.[
                  idx
                ] || {};

            const esFinanciera =
              pago.medioPago ===
              "CREDITO FINANCIERA";

            const esCreditoTarjeta =
              pago.medioPago ===
              "CREDITO";

            const financiera =
              esFinanciera
                ? getFinancieraCalculadoraById(
                    pago.financiera,
                    financieras
                  )
                : null;

            const config =
              financiera
                ? resolverConfiguracionFinancieraCalculadora(
                    financiera,
                    toNumber(
                      pago
                        .cuotasFinanciacion,
                      0
                    )
                  )
                : null;

            return (
              <div
                className="cr-payment-card"
                key={
                  pago.tempId
                }
              >

                {/* =========================================
                    HEADER PAGO
                ========================================= */}

                <div className="cr-payment-header">

                  <strong>
                    Pago {idx + 1}
                  </strong>

                  <button
                    type="button"
                    className="cr-btn-remove"
                    onClick={() =>
                      quitarPago(
                        idx
                      )
                    }
                    disabled={
                      pagos.length <=
                      1
                    }
                  >
                    ✕ Quitar
                  </button>

                </div>

                {/* =========================================
                    CAMPOS
                ========================================= */}

                <div className="cr-payment-grid">

                  {/* MEDIO */}

                  <div className="cr-field">

                    <label>
                      Medio de pago
                    </label>

                    <select
                      value={
                        pago.medioPago
                      }
                      onChange={(
                        e
                      ) =>
                        cambiarPago(
                          idx,
                          "medioPago",
                          e.target
                            .value
                        )
                      }
                    >

                      {MEDIOS_PAGO_VENTA.map(
                        (
                          medio
                        ) => (
                          <option
                            key={
                              medio
                            }
                            value={
                              medio
                            }
                          >
                            {
                              medio
                            }
                          </option>
                        )
                      )}

                    </select>

                  </div>

                  {/* FINANCIERA */}

                  {esFinanciera && (

                    <div className="cr-field cr-field-financiera">

                      <label>
                        Financiera
                      </label>

                      <select
                        value={
                          pago.financiera
                        }
                        onChange={(
                          e
                        ) =>
                          cambiarPago(
                            idx,
                            "financiera",
                            e.target
                              .value
                          )
                        }
                        disabled={
                          loadingFinancieras
                        }
                      >

                        <option value="">
                          {loadingFinancieras
                            ? "Cargando..."
                            : "Seleccionar financiera"}
                        </option>

                        {financieras.map(
                          (
                            f
                          ) => {
                            const id =
                              f._id ||
                              f.id;

                            const nombre =
                              clean(
                                f.financiera
                              ) ||
                              clean(
                                f.razonsocial
                              ) ||
                              `Financiera ${
                                f.codfinanciera ||
                                ""
                              }`;

                            return (
                              <option
                                key={
                                  id
                                }
                                value={
                                  id
                                }
                              >
                                {
                                  nombre
                                }
                              </option>
                            );
                          }
                        )}

                      </select>

                    </div>

                  )}

                  {/* CUOTAS */}

                  {esFinanciera &&
                    config
                      ?.requiereCuotas && (

                    <div className="cr-field">

                      <label>
                        Cuotas
                      </label>

                      <select
                        value={
                          pago
                            .cuotasFinanciacion
                        }
                        onChange={(
                          e
                        ) =>
                          cambiarPago(
                            idx,
                            "cuotasFinanciacion",
                            e.target
                              .value
                          )
                        }
                      >

                        <option value="">
                          Elegir
                        </option>

                        {config
                          .cuotasDisponibles
                          .map(
                            (
                              cantidad
                            ) => (
                              <option
                                key={
                                  cantidad
                                }
                                value={
                                  cantidad
                                }
                              >
                                {
                                  cantidad
                                }{" "}
                                cuotas
                              </option>
                            )
                          )}

                      </select>

                    </div>

                  )}

                  {/* MONTO */}

                  <div className="cr-field cr-field-money">

                    <label>
                      {esFinanciera
                        ? "Monto fondeado / cobrado"
                        : esCreditoTarjeta
                        ? "Importe procesado por Posnet"
                        : "Monto recibido"}
                    </label>

                    <div className="cr-money-input">

                      <span>
                        US$
                      </span>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={
                          pago
                            .montoCobradoUSD
                        }
                        onChange={(
                          e
                        ) =>
                          cambiarPago(
                            idx,
                            "montoCobradoUSD",
                            e.target
                              .value
                          )
                        }
                      />

                    </div>

                    {esCreditoTarjeta && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "#fff7ed",
                          border: "1px solid #fdba74",
                          color: "#9a3412",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.4,
                        }}
                      >
                        💳 Tarjeta de crédito común: ingresá
                        el importe procesado por el Posnet,
                        SIN agregar el 3% de recargo.
                        <br />
                        Ejemplo: para una venta de US$ 1.000,00
                        más TAX, ingresá US$ 1.070,00.
                        El Posnet cobra el 3% adicional
                        directamente al cliente.
                      </div>
                    )}

                    {/* {esFinanciera &&
                      toNumber(
                        pago.montoCobradoUSD,
                        0
                      ) > 0 && (

                      <button
                        type="button"
                        onClick={() =>
                          copiarMontoFinanciera(
                            pago
                          )
                        }
                        style={{
                          marginTop: 8,
                          width: "100%",
                          border: "1px solid #3b82f6",
                          borderRadius: 8,
                          padding: "8px 10px",
                          background: "#172554",
                          color: "#ffffff",
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        {montoCopiadoId ===
                        pago.tempId
                          ? "✓ Copiado"
                          : "📋 Copiar monto"}
                      </button>
                    )} */}

                  </div>

                </div>

                {/* =========================================
                    CONFIG FINANCIERA
                ========================================= */}

                {esFinanciera &&
                  config && (

                  <div className="cr-finance-config">

                    <div>

                      <span>
                        Fee
                      </span>

                      <strong>
                        {
                          config
                            .feePorcentaje
                        }
                        %
                      </strong>

                    </div>

                    <div>

                      <span>
                        Tax
                      </span>

                      <strong
                        className={
                          config.aplicaTax
                            ? "cr-tax-yes"
                            : "cr-tax-no"
                        }
                      >
                        {config.aplicaTax
                          ? "SÍ"
                          : "NO"}
                      </strong>

                    </div>

                    <div>

                      <span>
                        Cálculo
                      </span>

                      <strong>
                        {
                          config
                            .tipoCalculo
                        }
                      </strong>

                    </div>

                  </div>

                )}

                {/* =========================================
                    ERROR
                ========================================= */}

                {detalle?.error && (

                  <div className="cr-payment-error">

                    ⚠{" "}
                    {
                      detalle.error
                    }

                  </div>

                )}

                {/* =========================================
                    RESULTADO
                ========================================= */}

                {toNumber(
                  detalle
                    ?.montoCobradoUSD,
                  0
                ) > 0 &&
                  !detalle?.error && (

                  <div className="cr-payment-result">

                    <div>

                      <span>
                        {esCreditoTarjeta
                          ? "Importe procesado por Posnet"
                          : "Cobrado / Fondeado"}
                      </span>

                      <strong>
                        {formatUSD(
                          detalle
                            .montoCobradoUSD
                        )}
                      </strong>

                    </div>

                    <div>

                      <span>
                        {esCreditoTarjeta
                          ? "Recargo tarjeta (3%)"
                          : "Fee"}
                      </span>

                      <strong>
                        {formatUSD(
                          esCreditoTarjeta
                            ? detalle.recargoTarjetaUSD
                            : detalle.feeMontoUSD
                        )}
                      </strong>

                    </div>

                    <div>

                      <span>
                        Aplicado
                      </span>

                      <strong>
                        {formatUSD(
                          detalle
                            .montoAplicadoUSD
                        )}
                      </strong>

                    </div>

                    <div>

                      <span>
                        Tax
                      </span>

                      <strong>
                        {detalle
                          .aplicaTax
                          ? formatUSD(
                              detalle
                                .taxUSD
                            )
                          : "NO APLICA"}
                      </strong>

                    </div>

                    <div className="cr-payment-product">

                      <span>
                        Aporte al producto
                      </span>

                      <strong>
                        {formatUSD(
                          detalle
                            .precioProductoUSD
                        )}
                      </strong>

                    </div>

                  </div>

                )}

              </div>
            );
          }
        )}

      </div>

      {/* ===================================================
          RESUMEN
      =================================================== */}

      <div className="cr-summary">

        <div className="cr-summary-title">

          <span>
            RESULTADO
          </span>

          <small>
            Precio calculado según
            formas de pago
          </small>

        </div>

        <div className="cr-summary-grid">

          <div>

            <span>
              Total cobrado /
              fondeado
            </span>

            <strong>
              {formatUSD(
                resultado
                  ?.totalCobradoUSD
              )}
            </strong>

          </div>

          <div>

            <span>
              Fees
            </span>

            <strong>
              {formatUSD(
                resultado
                  ?.totalFeeUSD
              )}
            </strong>

          </div>

          <div>

            <span>
              Tax
            </span>

            <strong>
              {formatUSD(
                resultado
                  ?.totalTaxUSD
              )}
            </strong>

          </div>

          <div>

            <span>
              Total aplicado
            </span>

            <strong>
              {formatUSD(
                resultado
                  ?.totalAplicadoUSD
              )}
            </strong>

          </div>

        </div>

        <div className="cr-product-total">

          <span>
            PRECIO DE LA VENTA
            SIN IMPUESTOS
          </span>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <strong>
              {formatUSD(
                resultado
                  ?.precioProductoUSD
              )}
            </strong>

            {toNumber(
              resultado?.precioProductoUSD,
              0
            ) > 0 && (
              <button
                type="button"
                onClick={
                  copiarPrecioVenta
                }
                style={{
                  border:
                    "1px solid rgba(255,255,255,.65)",
                  borderRadius: 8,
                  padding: "7px 12px",
                  background:
                    "rgba(255,255,255,.12)",
                  color: "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {montoCopiadoId ===
                "PRECIO_VENTA"
                  ? "✓ Copiado"
                  : "📋 Copiar"}
              </button>
            )}
          </div>

        </div>

        {resultado
          ?.errores
          ?.length > 0 && (

          <div className="cr-summary-errors">

            {resultado
              .errores
              .map(
                (
                  error,
                  idx
                ) => (

                  <div
                    key={
                      idx
                    }
                  >
                    ⚠ {error}
                  </div>

                )
              )}

          </div>

        )}

      </div>

      {/* ===================================================
          ACCIONES
      =================================================== */}

      <div className="cr-actions">

        <button
          type="button"
          className="cr-btn-clear"
          onClick={
            limpiar
          }
        >
          Limpiar calculadora
        </button>

        {typeof onAplicar ===
        "function" ? (

          <button
            type="button"
            className="cr-btn-apply"
            onClick={
              aplicarAlPaso4
            }
            disabled={
              !puedeAplicar
            }
          >
            ✓ Aplicar al Paso 4
          </button>

        ) : (

          <div className="cr-safe-label">
            🔒 No modifica ninguna
            venta
          </div>

        )}

      </div>

      {typeof onAplicar ===
        "function" && (

        <div className="cr-apply-note">

          La calculadora no guarda la
          venta. Solo completa las formas
          de pago del Paso 4 para que el
          vendedor las revise antes de
          guardar.

        </div>

      )}

    </div>
  );
};

export default CalculadoraRiders;